/**
 * The refusals the authorization server answers a token request with that the portal can resolve on
 * the next pass: none of them is a refused account, so none ends on the denied page. `access_denied`
 * carries them as its `error_description`.
 *
 * - `mfa_enrolment_required`: the account has no second factor and the workspace requires one; the
 *   portal makes the person set it up.
 * - `sso_required`: the address is under an enforced connection and this session did not come
 *   through it; the portal routes the address to its identity provider.
 * - `sso_session_expired`: the session came through a connection, older than that connection allows.
 * - `session_revoked`: access was ended (a member removed or suspended, a password changed), so
 *   every session older than that moment is refused. A fresh sign-in is one.
 */
export const SIGN_IN_REFUSALS = {
  mfa_enrolment_required:
    'Two-factor authentication must be set up on your account before you can sign in.',
  sso_required:
    'This workspace signs its members in through its own identity provider. Sign in there to continue.',
  sso_session_expired:
    'Your single sign-on session has expired. Sign in through your identity provider again.',
  session_revoked: 'Your session was ended. Sign in again to continue.',
} as const;

export type SignInRefusal = keyof typeof SIGN_IN_REFUSALS;

/**
 * The refusals of the account itself: final, a retry would be refused again, so they end on the
 * denied page in plain words rather than as the server's token.
 *
 * - `account_deactivated`: the account is barred from signing in (deactivated by a workspace's
 *   directory, or blocked by an administrator). The API answers it too, on a token still in hand.
 */
export const ACCOUNT_REFUSALS = {
  account_deactivated: 'Your account has been deactivated by your organization.',
} as const;

export type AccountRefusal = keyof typeof ACCOUNT_REFUSALS;

/** The account refusal a string names, or `null` for anything else. */
export const asAccountRefusal = (value: unknown): AccountRefusal | null =>
  typeof value === 'string' && Object.hasOwn(ACCOUNT_REFUSALS, value)
    ? (value as AccountRefusal)
    : null;

/** The refusals for which the portal can route the address straight to its identity provider. */
const SSO_REFUSALS: ReadonlySet<SignInRefusal> = new Set<SignInRefusal>([
  'sso_required',
  'sso_session_expired',
]);

export const isSsoRefusal = (refusal: SignInRefusal | null): boolean =>
  !!refusal && SSO_REFUSALS.has(refusal);

/** The refusal a string names, or `null` for anything else (a real `access_denied`, say). */
export const asSignInRefusal = (value: unknown): SignInRefusal | null =>
  typeof value === 'string' && Object.hasOwn(SIGN_IN_REFUSALS, value)
    ? (value as SignInRefusal)
    : null;

/**
 * The refusal a failed code exchange states. The parsed body is the reliable place; the message is
 * read too because the OAuth client folds the description into it.
 */
export const signInRefusalFromError = (error: unknown): SignInRefusal | null => {
  if (!(error instanceof Error)) return null;
  const body = (error as { parsedBody?: { error_description?: unknown } }).parsedBody;
  const fromBody = asSignInRefusal(body?.error_description);
  if (fromBody) return fromBody;

  return (
    (Object.keys(SIGN_IN_REFUSALS) as SignInRefusal[]).find((refusal) =>
      error.message.includes(refusal),
    ) ?? null
  );
};
